/**
 * 偶像上線狀態的登記與輪詢（見 docs/idol-online-plan.md §4）
 *
 * - 頭像 mount 時 trackIdol 登記（計數 +1），unmount 時 untrackIdol（計數 -1）
 * - 新登記的 id 在 BATCH_DELAY_MS 內合併成一次請求，略過 CACHE_TTL_MS 內查過的
 * - 每 POLL_INTERVAL_MS 查一次所有登記中的 id；tab 在背景時跳過（新登記的合併查詢不受影響）
 * - 由 IdolOnlinePoller 依登入狀態 start / stop；未 start 前的登記會先排隊，start 時一起查
 *
 * 登記表放在模組層級而非 Redux，避免每個頭像 mount／unmount 都 dispatch。
 * 這是使用者沒有主動操作的背景請求：失敗不跳 StatusModal（使用者已同意），
 * 只 console.error 並清掉該批狀態；帶 isBackground，斷線時不觸發整頁 ServerErrorPage。
 */
import Axios from '@/shared/api/axios';
import { setOnlineStatuses, clearOnlineStatuses } from '@/shared/store/slices/onlineSlice';

const POLL_INTERVAL_MS = 30_000;
const CACHE_TTL_MS = 30_000;
const BATCH_DELAY_MS = 100;
// 後端單次上限
const MAX_IDS_PER_REQUEST = 100;

const refCounts = new Map();   // idolId → 目前畫面上的頭像數
const fetchedAt = new Map();   // idolId → 最近一次送出查詢的時間
const pending = new Set();     // 等待合併查詢的 idolId

let dispatch = null;           // start 時注入；為 null 代表未啟用
let flushTimer = null;
let pollTimer = null;
// 每次 stop 都 +1，讓 stop 之前送出、之後才回來的結果被丟棄（例如登出後才回來）
let generation = 0;

const fetchOnline = async (idolIds) => {
  const requestGeneration = generation;
  for (let i = 0; i < idolIds.length; i += MAX_IDS_PER_REQUEST) {
    const chunk = idolIds.slice(i, i + MAX_IDS_PER_REQUEST);
    const now = Date.now();
    chunk.forEach((idolId) => fetchedAt.set(idolId, now));

    try {
      // 逾時設成輪詢間隔：網路卡住時不會每次輪詢都疊一支，晚回來的舊結果也不會蓋掉新結果
      const res = await Axios.post(
        '/idol/check-online',
        { idol_ids: chunk },
        { isBackground: true, timeout: POLL_INTERVAL_MS }
      );
      if (requestGeneration !== generation) return;
      if (res.data.status_code === 'SYSTEM_1000') {
        dispatch(setOnlineStatuses(res.data.data));
      } else {
        console.error('[idolOnline] check-online failed:', res.data.status_code);
        chunk.forEach((idolId) => fetchedAt.delete(idolId));
        dispatch(clearOnlineStatuses(chunk));
      }
    } catch (error) {
      if (requestGeneration !== generation) return;
      console.error('[idolOnline] check-online failed:', error);
      chunk.forEach((idolId) => fetchedAt.delete(idolId));
      dispatch(clearOnlineStatuses(chunk));
    }
  }
};

// 新登記的 id 在背景 tab 也照常查（例如 cmd+點擊開在背景的分頁），只有輪詢在背景暫停
const flush = () => {
  flushTimer = null;
  if (!dispatch) return;
  const now = Date.now();
  const idolIds = [...pending].filter((idolId) => {
    const last = fetchedAt.get(idolId);
    return last === undefined || now - last >= CACHE_TTL_MS;
  });
  pending.clear();
  if (idolIds.length > 0) fetchOnline(idolIds);
};

const scheduleFlush = () => {
  if (flushTimer || !dispatch) return;
  flushTimer = setTimeout(flush, BATCH_DELAY_MS);
};

const poll = () => {
  // 背景 tab 跳過；回到前景後不立刻補查，等下一次輪詢或 WS 重連（剛從休眠喚醒時網路可能還沒恢復）
  if (document.hidden || refCounts.size === 0) return;
  pending.clear();
  fetchOnline([...refCounts.keys()]);
};

export const trackIdol = (idolId) => {
  const count = refCounts.get(idolId);
  refCounts.set(idolId, count === undefined ? 1 : count + 1);
  if (count === undefined) {
    pending.add(idolId);
    scheduleFlush();
  }
};

export const untrackIdol = (idolId) => {
  const count = refCounts.get(idolId);
  if (count === 1) {
    refCounts.delete(idolId);
    pending.delete(idolId);
  } else {
    refCounts.set(idolId, count - 1);
  }
};

/** 啟用：查一次所有登記中的 id，並開始輪詢 */
export const startIdolOnlineTracker = (storeDispatch) => {
  dispatch = storeDispatch;
  refCounts.forEach((_, idolId) => pending.add(idolId));
  scheduleFlush();
  pollTimer = setInterval(poll, POLL_INTERVAL_MS);
};

/** 停用：停止輪詢並作廢快取；登記表保留（頭像仍在畫面上，下次 start 會重新查） */
export const stopIdolOnlineTracker = () => {
  dispatch = null;
  generation += 1;
  clearInterval(pollTimer);
  clearTimeout(flushTimer);
  pollTimer = null;
  flushTimer = null;
  pending.clear();
  fetchedAt.clear();
};

/** WS 重連後立刻刷新所有登記中的 id（斷線期間的狀態變化都漏掉了） */
export const refreshTrackedIdols = () => {
  if (!dispatch || refCounts.size === 0) return;
  pending.clear();
  fetchOnline([...refCounts.keys()]);
};
